import { ImageFitStrategy } from "pages/media/types";
import {
  AvatarMask,
  AvatarShot,
  FixedScriptSpeech,
  GradientDefinition,
  MediaTemplateResponse,
  SilenceSpeech,
  TableTemplateItem,
  TemplateItem,
} from "types";
import { getNumberRoundedToPrecision, withFixedScriptIfNeeded } from ".";
import {
  DEFAULT_COLOR,
  DEFAULT_SPEECH_MAX_WORDS,
  DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  LOGO_PICTURE_PLACEHOLDER,
  MAX_TRANSITION_DURATION_IN_FRAMES,
  NULL_COLOR,
  VIDEO_FPS,
} from "../constants";
import {
  AnimationEffects,
  AnimationEffectValue,
  AvatarShape,
  BodyBackground,
  CustomText,
  Effects,
  Figure,
  Header,
  ItemWithLayerIndex,
  LINE_ORIENTATIONS,
  MainImage,
  Mask,
  MediaItem,
  Avatar,
  ParameteredText,
  Shape,
  SHAPE_TYPES,
  Stroke,
  Table,
  TemplateState,
  TableTextStyle,
  VideoScene,
} from "../types";
import { isMasked } from "./maskHelper";
import { generateEnterFromAttribute } from "./generators/attributeGenerators/EnterFrom";
import { generateLeaveToAttribute } from "./generators/attributeGenerators/LeaveTo";
import {
  generateAlignAttributes,
  generateCenteredPositionAttributes,
  generatePositionAttributes,
} from "./generators/attributeGenerators/Position";
import {
  getEffectDelayFrames,
  getEffectDurationFrames,
  getEffectMethod,
  isEffectActive,
  isEnterFromEffect,
  toFadeMethod,
} from "./animationHelpers";
import { generateShapeGraphics } from "./generators/graphicGenerators/shape";
import { generateLine } from "./generators/graphicGenerators/Line";
import { generateTextGraphics } from "./generators/graphicGenerators/Text";
import {
  generatePresenterItems,
  getAvatarWidthForGeneration,
  hasVisibleAvatar,
  isGradient,
  mapToServerColor,
} from "./itemsHelper";
import placeholders from "../../themeEditor/placeholders";
import { buildSlideInputs } from "./buildSlideInputs";
import { removeOrphanedSlides } from "./removeOrphanedSlides";

export const logoPlaceholder = LOGO_PICTURE_PLACEHOLDER;

export const generateBorder = (stroke?: Stroke) => {
  return {
    thickness: stroke?.width ?? 0,
    color: mapToServerColor(stroke?.color ?? NULL_COLOR),
    radius: getNumberRoundedToPrecision(stroke?.radius ?? 0),
  };
};

const hasBorder = (stroke?: Stroke) => !!(stroke?.color || stroke?.radius);

export const defaultAnchor = {
  horizontal_anchor: "center" as const,
  vertical_anchor: "center" as const,
};

export const generateRectangle = (
  color: string | GradientDefinition,
  stroke?: Stroke
): TemplateItem["graphics"] => {
  const rect: TemplateItem["graphics"] = {
    type: "Rectangle",
    color: isGradient(color) ? color : mapToServerColor(color),
    ...(hasBorder(stroke)
      ? {
          border: generateBorder(stroke),
        }
      : {}),
  };
  return rect;
};

export const generateFullScreenAttributes = (): TemplateItem["attributes"] => {
  return [
    {
      type: "FullScreen",
    },
  ];
};

export const getBackgroundBase = (): TemplateItem => ({
  name: "baseLayer",
  graphics: generateRectangle(DEFAULT_COLOR),
  attributes: generateFullScreenAttributes(),
  ...defaultAnchor,
});

export const sortItemsByLayerIndex = (
  items: (ItemWithLayerIndex | undefined | null)[]
): TemplateItem[] => {
  const filtered = items.filter((item): item is ItemWithLayerIndex => !!item);

  const sorted = filtered.toSorted((a, b) => a.layerIndex - b.layerIndex);

  const mapped = sorted.flatMap(({ item }) => {
    return Array.isArray(item) ? item : [item];
  });

  return mapped;
};

export const reconstructTableServerItem = (
  table: Table,
  serverItem: TemplateItem
): TemplateItem => {
  const tableGraphics = serverItem.graphics as Extract<
    TemplateItem["graphics"],
    { type: "Table" }
  >;

  const updatedComponents = tableGraphics.item_components
    .map(
      (component: TableTemplateItem["graphics"]["item_components"][number]) => {
        const comp = component as TemplateItem;
        if (
          comp.graphics.type === "ParameteredText" ||
          comp.graphics.type === "StreamedText"
        ) {
          return {
            ...comp,
            graphics: generateParameteredTextGraphics(
              table.textStyle,
              comp.graphics
            ),
            attributes: generateCommonAttributes(
              table.textStyle.position.x,
              table.textStyle.position.y,
              table.textStyle.size.width,
              table.textStyle.size.height ?? undefined,
              table.textStyle.animations,
              comp.attributes
            ),
          };
        }
        if (comp.graphics.type === "Line") {
          if (!table.showDividers) return null;
          return {
            ...comp,
            graphics: generateLine(table.dividerStyle),
            attributes: generateCommonAttributes(
              table.dividerStyle.position.x,
              table.dividerStyle.position.y,
              table.dividerStyle.size.width,
              table.dividerStyle.size.height,
              table.dividerStyle.animations,
              comp.attributes
            ),
          };
        }
        return comp;
      }
    )
    .filter(Boolean);

  const hasLine = updatedComponents.some((c) => c?.graphics.type === "Line");
  if (table.showDividers && !hasLine) {
    updatedComponents.push({
      name: "divider_line",
      graphics: generateLine(table.dividerStyle),
      attributes: generateCommonAttributes(
        table.dividerStyle.position.x,
        table.dividerStyle.position.y,
        table.dividerStyle.size.width,
        table.dividerStyle.size.height,
        table.dividerStyle.animations
      ),
      horizontal_anchor: "center",
      vertical_anchor: "center",
    });
  }

  return {
    ...serverItem,
    graphics: {
      ...tableGraphics,
      item_components: updatedComponents,
      time_interval:
        table.animationRowDelay != null
          ? Math.round(table.animationRowDelay * VIDEO_FPS)
          : tableGraphics.time_interval,
    },
    attributes: generateCommonAttributes(
      table.position.x,
      table.position.y,
      table.size.width,
      table.size.height,
      undefined,
      serverItem.attributes
    ),
  };
};

export const getItems = (
  {
    header,
    background,
    presenter,
    logo,
    images,
    videos,
    customTexts,
    shapes,
    parameteredTexts,
    mainImage,
    figure,
    slideLayoutShapes,
    table,
  }: TemplateState,
  aspectRatio: number,
  serverItems: TemplateItem[],
  keepHiddenItems = false,
  detectCenteredPositions = false
) => {
  const items: (ItemWithLayerIndex | undefined)[] = [];

  if (
    (header.background || header.banner?.assetId) &&
    logo.showLogo &&
    logo.assetId
  ) {
    items.push({
      item: {
        ...(serverItems.find((item) => item.name === "header") ??
          defaultAnchor),
        name: "header",
        graphics: generateItemHeaderGraphics(header),
        attributes: generateHeaderAttributes(
          header.size.width,
          header.size.height,
          header.position.y
        ),
      },
      layerIndex: header.layerIndex,
    });
  }

  if (logo.showLogo && logo.assetId) {
    const serverLogoItem = serverItems.find((item) => item.name === "logo");
    items.push({
      item: {
        ...(serverLogoItem ?? defaultAnchor),
        name: "logo",
        graphics: generateImage(logoPlaceholder),
        attributes: generateCommonAttributes(
          logo.position.x,
          logo.position.y,
          logo.size.width,
          logo.size.height,
          undefined,
          serverLogoItem?.attributes,
          logo.rotation,
          detectCenteredPositions
        ),
      },
      layerIndex: logo.layerIndex,
    });
  }

  let bodyMedia: TemplateItem | undefined;
  if (background.bodyView === "Video" && background.bodyVideoAsset) {
    bodyMedia = {
      graphics: generateCustomAssetVideo(background.bodyVideoAsset),
      attributes: generateFullScreenAttributes(),
      ...defaultAnchor,
    };
  } else if (background.bodyView === "Picture" && background.bodyImageAsset) {
    bodyMedia = {
      graphics: generateImage(background.bodyImageAsset, "Cover"),
      attributes: generateFullScreenAttributes(),
      ...defaultAnchor,
    };
  }

  items.push(
    generatePresenterItems(presenter, aspectRatio, detectCenteredPositions)
  );

  const { bodyRectangle, imageItems, videoItems, customTextItems, shapeItems } =
    generateCommonItems({
      storeBackground: background,
      images,
      videos,
      customTexts,
      shapes: [...shapes, ...(slideLayoutShapes ?? [])],
      serverItems,
    });
  items.push(...imageItems, ...videoItems, ...customTextItems, ...shapeItems);

  if (parameteredTexts?.length) {
    items.push(
      ...generateParameteredTextItems(
        parameteredTexts,
        serverItems,
        keepHiddenItems
      )
    );
  }

  if (mainImage && (keepHiddenItems || mainImage.visible)) {
    items.push(
      generateMainImageItem(
        mainImage,
        serverItems.find((item) => item.name === "main_image"),
        keepHiddenItems
      )
    );
  }

  if (figure) {
    items.push(...generateFigureItems(figure, serverItems, keepHiddenItems));
  }

  if (table) {
    const tableServerItem = serverItems.find(
      (item) => item.graphics.type === "Table"
    );
    if (tableServerItem) {
      items.push({
        item: reconstructTableServerItem(table, tableServerItem),
        layerIndex: table.layerIndex,
      });
    }
  }

  const sortedItems = sortItemsByLayerIndex(items);

  const backgroundItem = bodyMedia ?? bodyRectangle ?? getBackgroundBase();

  return [backgroundItem, ...sortedItems];
};

// Builds the transition's speech payload so the save always states the
// current choice explicitly. Mirrors generateSpeech (Custom projects): a
// missing speech is saved as Silence with a duration.
const buildTransitionSpeech = (
  transition: VideoScene
): FixedScriptSpeech | SilenceSpeech | undefined => {
  if (transition.speechType === "FixedScript") {
    return {
      type: "FixedScript",
      text: "{chapter.topic}",
      max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
    };
  }
  // Other types (e.g. SoundEffect on BE-made templates) aren't editable here, keep the server's speech
  if (transition.speechType !== undefined && transition.speechType !== "Silence") {
    return undefined;
  }
  const duration = transition.duration ?? transition.assetDuration;
  return {
    type: "Silence",
    max_subtitles_words: DEFAULT_SPEECH_MAX_WORDS,
    duration:
      duration !== undefined
        ? Math.min(duration, MAX_TRANSITION_DURATION_IN_FRAMES)
        : DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  };
};

export const mapTemplateStateToServerTemplate = (
  slideId: string,
  templateState: TemplateState,
  serverTemplate: MediaTemplateResponse["template"],
  aspectRatio: number,
  validSlideUuids: Set<string>,
  isTransitionSlide: boolean,
  themeLayoutItems?: TemplateItem[]
) => {
  const { music, presenter } = templateState;

  const slides = serverTemplate.slides;
  if (!slides || slides.length === 0) {
    console.error("No server slides for selected template:", {
      serverTemplate,
    });
    return;
  }

  const currentSlideIndex = slides.findIndex((slide) => slide.name === slideId);
  const currentServerSlide = slides[currentSlideIndex];
  if (!currentServerSlide) {
    console.error("No server slide for selected slide:", { slideId });
    return;
  }

  // Fill gaps in the server slide's sprite definitions with theme layout
  // sprites (by name). Items re-enabled via the text/media slot toggles have
  // no corresponding server item since they were filtered out on a prior
  // save; the theme sprite is their authoritative base for server mapping.
  const serverSpriteDefinitions = themeLayoutItems?.length
    ? [
        ...currentServerSlide.sprite_definitions,
        ...themeLayoutItems.filter(
          (themeItem) =>
            !!themeItem.name &&
            !currentServerSlide.sprite_definitions.some(
              (serverItem) => serverItem.name === themeItem.name
            )
        ),
      ]
    : currentServerSlide.sprite_definitions;

  // Heal the slide's inputs dict by overwriting the canonical keys derived
  // from the slide layout type. Existing extras are preserved so we don't
  // accidentally drop anything the server still relies on.
  const canonicalInputs = templateState.slideLayoutType
    ? buildSlideInputs(slideId, templateState.slideLayoutType)
    : undefined;

  const mappedServerSlide = {
    ...currentServerSlide,
    sprite_definitions: getItems(
      templateState,
      aspectRatio,
      serverSpriteDefinitions
    ),
    ...(canonicalInputs && {
      inputs: { ...currentServerSlide.inputs, ...canonicalInputs },
    }),
  };

  const updatedSlides = slides.toSpliced(
    currentSlideIndex,
    1,
    mappedServerSlide
  );

  // avatar_shot is shared across all slides in the template, not per-slide.
  const anySlideHasAvatar = updatedSlides.some((slide) =>
    slide.sprite_definitions.some((item) => item.graphics.type === "Avatar")
  );

  let avatarShot: AvatarShot = null;
  if (anySlideHasAvatar) {
    avatarShot = hasVisibleAvatar(presenter)
      ? presenter.avatarShot
      : serverTemplate.avatar_shot;
  }

  // This function only ever saves a Chapter or a Transition slide (Intro/
  // Closing go through generateTemplate/generateSpeech instead, which already
  // gate on type). For a Chapter, speech is left undefined so the `speech`
  // key is omitted from the payload below, leaving whatever the server
  // already has untouched.
  const speech = isTransitionSlide
    ? buildTransitionSpeech(
        withFixedScriptIfNeeded(templateState.transition, anySlideHasAvatar)
      )
    : undefined;

  return removeOrphanedSlides(
    {
      ...serverTemplate,
      ...(music && { music }),
      ...(speech !== undefined && { speech }),
      avatar_shot: avatarShot,
      slides: updatedSlides,
    },
    validSlideUuids
  );
};

export const generateItemHeaderGraphics = (header: Header) => {
  if (header.view === "Picture" && header.banner?.assetId)
    return generateImage(header.banner.assetId, "Cover", false);
  return generateRectangle(header.background ?? DEFAULT_COLOR);
};

const generateImageItems = (
  images: MediaItem[],
  serverItems?: TemplateItem[]
) => {
  return images.flatMap((image) => {
    const imageItem: TemplateItem = {
      name: image.id,
      graphics: generateImage(
        image.assetId,
        "Cover",
        false,
        image.stroke,
        isMasked(image.mask) ? generateCropMask(image.mask) : null
      ),
      attributes: generateCommonAttributes(
        image.position.x,
        image.position.y,
        image.size.width,
        image.size.height,
        image.animations,
        serverItems?.find((item) => item.name === image.id)?.attributes,
        image.rotation
      ),
      ...defaultAnchor,
    };

    return { item: imageItem, layerIndex: image.layerIndex };
  });
};

const generateVideoItems = (
  videos: MediaItem[],
  serverItems?: TemplateItem[]
) => {
  return videos.flatMap((video) => {
    const videoItem: TemplateItem = {
      name: video.id,
      graphics: generateCustomAssetVideo(
        video.assetId,
        undefined,
        video.stroke,
        isMasked(video.mask) ? generateCropMask(video.mask) : null
      ),
      attributes: generateCommonAttributes(
        video.position.x,
        video.position.y,
        video.size.width,
        video.size.height,
        video.animations,
        serverItems?.find((item) => item.name === video.id)?.attributes,
        video.rotation
      ),
      ...defaultAnchor,
    };
    return { item: videoItem, layerIndex: video.layerIndex };
  });
};

const generateCustomTextItems = (
  customTexts: CustomText[],
  serverItems?: TemplateItem[]
) => {
  return customTexts.flatMap((customText) => {
    const customTextItem: TemplateItem = {
      name: customText.id,
      graphics: generateTextGraphics(customText),
      attributes: generateCommonAttributes(
        customText.position.x,
        customText.position.y,
        customText.size.width,
        customText.size.height,
        customText.animations,
        serverItems?.find((item) => item.name === customText.id)?.attributes,
        customText.rotation
      ),
      ...defaultAnchor,
    };
    return { item: customTextItem, layerIndex: customText.layerIndex };
  });
};

const generateShapeItems = (shapes: Shape[], serverItems?: TemplateItem[]) => {
  return shapes.flatMap((shape) => {
    const serverItem = serverItems?.find((item) => item.name === shape.id);
    const graphics = generateShapeGraphics(shape);
    const isLine = shape.shapeType === SHAPE_TYPES.LINE;
    const size = isLine
      ? {
          width:
            shape.orientation === LINE_ORIENTATIONS.HORIZONTAL
              ? shape.size.width
              : undefined,
          height:
            shape.orientation === LINE_ORIENTATIONS.VERTICAL
              ? shape.size.height
              : undefined,
        }
      : {
          width: shape.size.width,
          height: shape.size.height,
        };

    // For lines, adjust position to the center of the thickness axis
    // (server renders lines centered on position, preview renders from top-left)
    const position = isLine
      ? {
          x:
            shape.orientation === LINE_ORIENTATIONS.VERTICAL
              ? shape.position.x + shape.size.width / 2
              : shape.position.x,
          y:
            shape.orientation === LINE_ORIENTATIONS.HORIZONTAL
              ? shape.position.y + shape.size.height / 2
              : shape.position.y,
        }
      : shape.position;

    const shapeItem: TemplateItem = {
      ...(serverItem ?? defaultAnchor),
      name: shape.id,
      graphics,
      attributes: generateCommonAttributes(
        position.x,
        position.y,
        size.width,
        size.height,
        shape.animations,
        isLine ? undefined : serverItem?.attributes,
        shape.rotation
      ),
    };

    return {
      item: shapeItem,
      layerIndex: shape.layerIndex,
    };
  });
};

export const generateImage = (
  assetId: string,
  strategy: ImageFitStrategy = "Contain",
  keep_ratio?: boolean,
  stroke?: Stroke,
  mask: AvatarMask = null
): TemplateItem["graphics"] => {
  return {
    type: "AssetImage",
    mask, // a Rectangle crop mask when the item is cropped, else null
    asset_id: assetId,
    keep_ratio: keep_ratio ?? true, // true for all images
    fit: {
      strategy,
      horizontal_align: "center",
      vertical_align: "center",
    },
    ...(hasBorder(stroke)
      ? {
          border: generateBorder(stroke),
        }
      : {}),
  };
};

/**
 * A rectangular crop mask: a white Rectangle sub-region of the item's box (the
 * sprite). Its alpha exposes only the cropped window; positioned at the mask's
 * top-left and sized to its fraction of the box. Mirrors `generateAvatarMask`.
 */
export const generateCropMask = (mask: Mask): AvatarMask => ({
  shape:
    mask.shape === "ellipse"
      ? { type: "Circle", color: [255, 255, 255, 255] }
      : { type: "Rectangle", color: [255, 255, 255, 255] },
  attributes: [
    { type: "Position", position: generateAlignAttributes(mask.x, mask.y) },
    { type: "Width", rate: getNumberRoundedToPrecision(mask.width) },
    { type: "Height", rate: getNumberRoundedToPrecision(mask.height) },
  ],
});

export const generateCircle = (
  color: string | GradientDefinition,
  stroke?: Stroke
): TemplateItem["graphics"] => {
  return {
    type: "Circle",
    color: isGradient(color) ? color : mapToServerColor(color),
    ...(hasBorder(stroke)
      ? {
          border: generateBorder(stroke),
        }
      : {}),
  };
};

export const generateCustomAssetVideo = (
  assetId: string,
  strategy: ImageFitStrategy = "Cover",
  stroke?: Stroke,
  mask: AvatarMask = null,
  loop = true
): TemplateItem["graphics"] => {
  return {
    type: "CustomAssetVideo",
    asset_id: assetId,
    loop,
    fit: {
      strategy,
      horizontal_align: "center",
      vertical_align: "center",
    },
    ...(mask ? { mask } : {}),
    ...(hasBorder(stroke)
      ? {
          border: generateBorder(stroke),
        }
      : {}),
  };
};

export const generateHeaderAttributes = (
  widthRatio = 1,
  heightRatio = 0.1,
  verticalRatio = 0
): TemplateItem["attributes"] => {
  return [
    {
      type: "Position",
      position: {
        type: "Align",
        vertical: "none",
        horizontal: "center",
        vertical_ratio: verticalRatio,
      },
    },
    {
      type: "Width",
      rate: widthRatio,
    },
    {
      type: "Height",
      rate: heightRatio,
    },
  ];
};

/**
 * Static rotation (degrees, clockwise) → a degenerate `LinearRotation`
 * attribute with equal from/to angles. The renderer rotates counter-clockwise,
 * so the sign is flipped to match the editor's clockwise CSS preview.
 */
export const generateRotationAttribute = (
  rotation: number
): NonNullable<TemplateItem["attributes"]>[number] => {
  const angle = -rotation;
  return {
    type: "LinearRotation",
    start: 0,
    duration: 1,
    from_angle: angle,
    to_angle: angle,
  };
};

export const generateCommonAttributes = (
  x: number,
  y: number,
  width?: number,
  height?: number,
  animations?: AnimationEffects,
  serverItemAttributes?: TemplateItem["attributes"],
  rotation?: number,
  detectCenteredPositions = false
) => {
  const hasEnterFromAnimation =
    !!animations &&
    Object.entries(animations).some(
      ([effect, value]) =>
        isEnterFromEffect(effect as Effects) && isEffectActive(value)
    );

  const attributes: TemplateItem["attributes"] = [];

  // Position is always the item's UNROTATED top-left — the renderer rotates the
  // sprite around its own center (Libertyfi.api MED-6725), so no rotation-aware
  // position adjustment is needed (a rotated item is positioned exactly like a
  // non-rotated one).

  // When an enterFrom animation is active, the item's resting position is
  // encoded in the Move animation's `to_position` — adding a static Position
  // attribute alongside would duplicate (and potentially conflict with) it.
  // Removing the animation re-emits a Position from the current x/y on the
  // next save (itemsHelper recovers x/y from `to_position` on load).
  if (!hasEnterFromAnimation) {
    if (
      detectCenteredPositions &&
      width !== undefined &&
      height !== undefined
    ) {
      attributes.push(generateCenteredPositionAttributes(x, y, width, height));
    } else {
      attributes.push(generatePositionAttributes(x, y, width, height));
    }
  }

  if (width) {
    attributes.push({
      type: "Width",
      rate: getNumberRoundedToPrecision(width),
    });
  }

  if (height) {
    attributes.push({
      type: "Height",
      rate: getNumberRoundedToPrecision(height),
    });
  }

  // Emit a static rotation only when set; a 0/undefined rotation drops any
  // existing LinearRotation (see the preservation filter below).
  if (rotation) {
    attributes.push(generateRotationAttribute(rotation));
  }

  if (animations) {
    const animationHandlers: Record<
      Effects,
      (
        effectValue: AnimationEffectValue
      ) => NonNullable<TemplateItem["attributes"]>[number]
    > = {
      [Effects.fadeIn]: generateFadeInAttributes,
      [Effects.fadeOut]: generateFadeOutAttributes,
      [Effects.enterFromTop]: (ev) =>
        generateEnterFromAttribute(
          Effects.enterFromTop,
          x,
          y,
          ev,
          width,
          height
        ),
      [Effects.enterFromBottom]: (ev) =>
        generateEnterFromAttribute(
          Effects.enterFromBottom,
          x,
          y,
          ev,
          width,
          height
        ),
      [Effects.enterFromLeft]: (ev) =>
        generateEnterFromAttribute(
          Effects.enterFromLeft,
          x,
          y,
          ev,
          width,
          height
        ),
      [Effects.enterFromRight]: (ev) =>
        generateEnterFromAttribute(
          Effects.enterFromRight,
          x,
          y,
          ev,
          width,
          height
        ),
      [Effects.leaveToTop]: (ev) =>
        generateLeaveToAttribute(Effects.leaveToTop, x, y, ev, width, height),
      [Effects.leaveToBottom]: (ev) =>
        generateLeaveToAttribute(
          Effects.leaveToBottom,
          x,
          y,
          ev,
          width,
          height
        ),
      [Effects.leaveToLeft]: (ev) =>
        generateLeaveToAttribute(Effects.leaveToLeft, x, y, ev, width, height),
      [Effects.leaveToRight]: (ev) =>
        generateLeaveToAttribute(Effects.leaveToRight, x, y, ev, width, height),
    };

    const filteredAnimations = Object.entries(animations).filter(
      (entry): entry is [Effects, AnimationEffectValue] =>
        isEffectActive(entry[1]) && entry[0] in animationHandlers
    );
    for (const [key, value] of filteredAnimations) {
      attributes.push(animationHandlers[key](value));
    }
  }

  const animationAttributeTypes = ["Fade", "LinearMove", "Move"];
  const callerOwnsAnimations = animations !== undefined;
  // When the caller passes a rotation it owns the LinearRotation attribute, so
  // a stale server one is dropped (or replaced by the freshly pushed one).
  const callerOwnsRotation = rotation !== undefined;
  const existingAttributesNotMapped = serverItemAttributes?.filter(
    (attribute) => {
      if (attributes.some((a) => a.type === attribute.type)) return false;
      if (
        callerOwnsAnimations &&
        animationAttributeTypes.includes(attribute.type)
      )
        return false;
      if (callerOwnsRotation && attribute.type === "LinearRotation")
        return false;
      if (hasEnterFromAnimation && attribute.type === "Position") return false;
      return true;
    }
  );
  if (existingAttributesNotMapped) {
    attributes.push(...existingAttributesNotMapped);
  }

  return attributes;
};

const generateFadeInAttributes = (
  effectValue: AnimationEffectValue
): NonNullable<TemplateItem["attributes"]>[number] => {
  const duration = getEffectDurationFrames(effectValue);
  const method = toFadeMethod(getEffectMethod(effectValue));
  const startFrame = getEffectDelayFrames(effectValue, 0);
  return {
    type: "Fade",
    start: { type: "Frame", frame: startFrame },
    duration,
    from_opacity: 0,
    to_opacity: 255,
    method,
  };
};

const generateFadeOutAttributes = (
  effectValue: AnimationEffectValue
): NonNullable<TemplateItem["attributes"]>[number] => {
  const duration = getEffectDurationFrames(effectValue);
  const method = toFadeMethod(getEffectMethod(effectValue));
  const startFrame = getEffectDelayFrames(effectValue, -duration);
  return {
    type: "Fade",
    start: { type: "Frame", frame: startFrame },
    duration,
    from_opacity: 255,
    to_opacity: 0,
    method,
  };
};

export const generateAvatarMask = (avatarShape: AvatarShape): AvatarMask => {
  if (avatarShape === AvatarShape.Circle)
    return {
      shape: {
        type: "Circle",
        color: [255, 255, 255, 255],
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            vertical: "center",
            horizontal: "center",
          },
        },
        {
          type: "Height",
          rate: 1,
        },
      ],
    };
  else
    return {
      shape: {
        type: "Rectangle",
        color: [255, 255, 255, 255],
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            vertical: "center",
            horizontal: "center",
          },
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Width",
          rate: 1,
        },
      ],
    };
};

export const generateAvatarShapeItem = (
  presenter: Avatar,
  aspectRatio?: number,
  detectCenteredPositions = false
): TemplateItem => {
  let shapeBackground: string | GradientDefinition = NULL_COLOR;
  if (presenter.avatarShape !== AvatarShape.Regular) {
    shapeBackground = presenter.background ?? NULL_COLOR;
  }

  return {
    name: "avatar_background",
    graphics:
      presenter.avatarShape === AvatarShape.Circle
        ? generateCircle(shapeBackground, presenter.stroke)
        : generateRectangle(shapeBackground, presenter.stroke),
    attributes: generateCommonAttributes(
      presenter.position.x,
      presenter.position.y,
      getAvatarWidthForGeneration(presenter, aspectRatio),
      presenter.size.height,
      presenter.animations,
      undefined,
      presenter.rotation,
      detectCenteredPositions
    ),
    ...defaultAnchor,
  };
};

const generateBackgroundColorRectangle = (
  color: string | GradientDefinition
): TemplateItem => {
  return {
    graphics: generateRectangle(color),
    attributes: generateFullScreenAttributes(),
    ...defaultAnchor,
  };
};

const generateParameteredTextGraphics = (
  text: ParameteredText | TableTextStyle,
  serverGraphics: Extract<
    TemplateItem["graphics"],
    { type: "StreamedText" | "ParameteredText" }
  >
): TemplateItem["graphics"] => {
  const sharedStyle = {
    font: text.fontAssetId ?? serverGraphics.font,
    size: text.fontSize,
    color: text.color ? mapToServerColor(text.color) : serverGraphics.color,
    upper_case: text.upperCase ?? false,
    background: text.backgroundColor
      ? mapToServerColor(text.backgroundColor)
      : null,
    wrap_text: text.wrapText ?? true,
    align: text.textAlign,
    adjust_font: text.adjustFont ?? false,
    stroke_color: text.strokeColor ? mapToServerColor(text.strokeColor) : null,
    vertical_align: text.verticalAlign ?? "top",
    line_spacing: text.lineHeight,
  };

  if (text.animated) {
    if (serverGraphics.type === "StreamedText") {
      return { ...serverGraphics, ...sharedStyle };
    }
    return {
      type: "StreamedText",
      content: serverGraphics.parameters,
      words_per_second: 25,
      effects: null,
      ...sharedStyle,
    };
  }

  if (serverGraphics.type === "ParameteredText") {
    return { ...serverGraphics, ...sharedStyle };
  }

  if (typeof serverGraphics.content === "object") {
    return {
      type: "ParameteredText",
      parameters: serverGraphics.content,
      format: "",
      ...sharedStyle,
    };
  }

  return { ...serverGraphics, ...sharedStyle };
};

const generateParameteredTextItems = (
  parameteredTexts: ParameteredText[],
  serverItems: TemplateItem[],
  keepHiddenItems = false
): ItemWithLayerIndex[] => {
  const source = keepHiddenItems
    ? parameteredTexts
    : parameteredTexts.filter((text) => text.visible);
  return source.flatMap((text) => {
    const serverItem = serverItems.find((item) => item.name === text.id);
    if (
      !serverItem ||
      (serverItem.graphics.type !== "StreamedText" &&
        serverItem.graphics.type !== "ParameteredText")
    )
      return [];
    return [
      {
        item: {
          ...serverItem,
          graphics: generateParameteredTextGraphics(text, serverItem.graphics),
          attributes: generateCommonAttributes(
            text.position.x,
            text.position.y,
            text.size.width,
            text.size.height ?? undefined,
            text.animations,
            undefined,
            text.rotation
          ),
        },
        layerIndex: text.layerIndex,
      },
    ];
  });
};

const generateMainImageItem = (
  mainImage: MainImage,
  serverItem: TemplateItem | undefined,
  keepHiddenItems = false
): ItemWithLayerIndex | undefined => {
  const baseItem = serverItem ?? placeholders.MAIN_IMAGE_ITEM;
  if (
    (!serverItem ||
      (serverItem.graphics.type !== "AssetImage" &&
        serverItem.graphics.type !== "CustomAssetVideo")) &&
    !keepHiddenItems
  )
    return;
  return {
    item: {
      ...baseItem,
      graphics: generateMainImageGraphics(mainImage, baseItem.graphics),
      attributes: generateCommonAttributes(
        mainImage.position.x,
        mainImage.position.y,
        mainImage.size.width,
        mainImage.size.height,
        mainImage.animations,
        serverItem?.attributes ?? placeholders.MAIN_IMAGE_ITEM.attributes,
        mainImage.rotation
      ),
    },
    layerIndex: mainImage.layerIndex,
  };
};

// When the user has replaced the slide's main media (assetId !== "default"),
// write the literal asset_id (and the right graphics shape) onto the saved
// item so it survives the round-trip. Otherwise preserve the original
// parameter-ref so the backend keeps resolving the slide placeholder.
const generateMainImageGraphics = (
  mainImage: MainImage,
  originalGraphics: TemplateItem["graphics"]
): TemplateItem["graphics"] => {
  const isReplaced = mainImage.assetId !== "default";
  if (!isReplaced) return originalGraphics;

  const mask = "mask" in originalGraphics ? originalGraphics.mask : null;
  const border = "border" in originalGraphics ? originalGraphics.border : null;
  // The main image is now resized freely like a regular image, so crop to fill
  // (Cover) to match the editor's object-fit: cover preview, preserving any
  // alignment the theme defined.
  const originalFit =
    "fit" in originalGraphics ? originalGraphics.fit : undefined;
  const horizontalAlign = originalFit?.horizontal_align ?? "center";
  const verticalAlign = originalFit?.vertical_align ?? "center";

  if (mainImage.mediaType === "video") {
    return {
      type: "CustomAssetVideo",
      asset_id: mainImage.assetId,
      loop: false,
      mask,
      fit: {
        strategy: "Cover",
        horizontal_align: horizontalAlign,
        vertical_align: verticalAlign,
      },
      border,
    };
  }
  return {
    type: "AssetImage",
    asset_id: mainImage.assetId,
    keep_ratio: false,
    mask,
    fit: {
      strategy: "Cover",
      horizontal_align: horizontalAlign,
      vertical_align: verticalAlign,
    },
    border,
  };
};

const generateFigureItems = (
  figure: Figure,
  serverItems: TemplateItem[],
  keepHiddenItems = false
): ItemWithLayerIndex[] => {
  if (!keepHiddenItems && !figure.number.visible) return [];

  const numberServerItem = serverItems.find(
    (item) => item.name === figure.number.id
  );
  const numberItem: ItemWithLayerIndex | undefined =
    numberServerItem?.graphics.type === "ParameteredText" ||
    numberServerItem?.graphics.type === "StreamedText"
      ? {
          item: {
            ...numberServerItem,
            graphics: generateParameteredTextGraphics(
              figure.number,
              numberServerItem.graphics
            ),
            attributes: generateCommonAttributes(
              figure.number.position.x,
              figure.number.position.y,
              figure.number.size.width,
              figure.number.size.height ?? undefined,
              figure.number.animations,
              numberServerItem.attributes,
              figure.number.rotation
            ),
            ...defaultAnchor,
          },
          layerIndex: figure.number.layerIndex,
        }
      : undefined;
  if (!numberItem) return [];

  const items: ItemWithLayerIndex[] = [numberItem];

  const serverUnitsItem = serverItems.find(
    (item) => item.name === figure.units?.id
  );
  if (
    figure.units &&
    (keepHiddenItems || figure.units.visible) &&
    serverUnitsItem &&
    serverUnitsItem.graphics.type === "Switch"
  ) {
    const unitText = figure.units;
    // Switch sub-items are typed as `unknown[]`, so we cast.
    const optionPercentBase = serverUnitsItem.graphics.options["%"]?.[0] as
      | TemplateItem
      | undefined;
    const defaultBase = serverUnitsItem.graphics.default?.[0] as
      | TemplateItem
      | undefined;
    const graphicsBase = optionPercentBase ?? defaultBase;

    if (graphicsBase) {
      const unitGraphics = generateParameteredTextGraphics(
        unitText,
        graphicsBase.graphics as Extract<
          TemplateItem["graphics"],
          { type: "ParameteredText" }
        >
      );

      const buildSlotItem = (base: TemplateItem): TemplateItem => ({
        ...base,
        graphics: unitGraphics,
        attributes: generateCommonAttributes(
          unitText.position.x,
          unitText.position.y,
          unitText.size.width,
          unitText.size.height ?? undefined,
          unitText.animations,
          base.attributes,
          unitText.rotation
        ),
      });

      const switchItem: ItemWithLayerIndex = {
        item: {
          ...serverUnitsItem,
          graphics: {
            ...serverUnitsItem.graphics,
            options: {
              ...serverUnitsItem.graphics.options,
              ...(optionPercentBase
                ? { "%": [buildSlotItem(optionPercentBase)] }
                : {}),
            },
            ...(defaultBase ? { default: [buildSlotItem(defaultBase)] } : {}),
          },
        },
        layerIndex: unitText.layerIndex,
      };
      items.push(switchItem);
    }
  }

  return items;
};

export const generateCommonItems = ({
  storeBackground,
  images,
  videos,
  customTexts,
  shapes,
  serverItems,
}: {
  storeBackground: BodyBackground;
  images: MediaItem[];
  videos: MediaItem[];
  customTexts: CustomText[];
  shapes: Shape[];
  serverItems?: TemplateItem[];
}) => {
  const result: {
    bodyRectangle: TemplateItem | null;
    imageItems: ItemWithLayerIndex[];
    videoItems: ItemWithLayerIndex[];
    customTextItems: ItemWithLayerIndex[];
    shapeItems: ItemWithLayerIndex[];
  } = {
    bodyRectangle: null,
    imageItems: generateImageItems(images, serverItems),
    videoItems: generateVideoItems(videos, serverItems),
    customTextItems: generateCustomTextItems(customTexts, serverItems),
    shapeItems: generateShapeItems(shapes, serverItems),
  };

  if (storeBackground.bodyColor) {
    result.bodyRectangle = generateBackgroundColorRectangle(
      storeBackground.bodyColor
    );
  }

  return result;
};
